<template>
  <section v-if="threads.length" class="text-tools" :class="{ 'unassigned-console': unassigned }">
    <component :is="unassigned ? 'MessageToolsDrawer' : 'div'">
    <slot name="tools" />
    <details>
      <summary>Text conversation controls <span v-if="unread">· {{ unread }} unread</span></summary>
      <div v-for="thread in threads" :key="thread.id" class="thread-tools">
        <span>{{ thread.maskedPhone }} · {{ thread.conversationStatus }}{{ thread.optOutStatus === 'stopped' ? ' · Text opted out' : '' }}</span>
        <button :disabled="busy" @click="markRead(thread.id)">Mark read</button>
        <button :disabled="busy" @click="archive(thread)">{{ thread.conversationStatus === 'archived' ? 'Restore texts' : 'Archive texts' }}</button>
        <button :disabled="busy" @click="deleteId = thread.id; confirmation = ''">Delete texts…</button>
      </div>
      <div v-if="deleteId" class="delete-confirm">
        <p>Delete the stored SMS thread permanently? Unified sent-message records, app messages, and email history are retained. Type CONFIRM to continue.</p>
        <input v-model="confirmation" aria-label="Deletion confirmation" placeholder="CONFIRM" :disabled="busy">
        <button :disabled="busy || confirmation !== 'CONFIRM'" @click="remove">Delete texts</button>
        <button :disabled="busy" @click="deleteId = ''">Cancel</button>
      </div>
    </details>
    </component>
    <p v-if="error" role="alert">{{ error }}</p>
    <template v-if="unassigned">
      <p class="hint">Unassigned number · {{ threads[0].maskedPhone }}</p>
      <p v-if="threads[0].optOutStatus === 'stopped'">This number has opted out of texts.</p>
      <div class="sms-feed">
        <article v-for="message in messages" :key="message.id" :class="message.direction">
          <p>{{ message.text }}</p><small>{{ new Date(message.occurredAt).toLocaleString() }} · {{ message.status }}</small>
          <small v-if="message.numMedia"> · {{ message.numMedia }} media attachment(s)</small>
        </article>
      </div>
      <form @submit.prevent="send">
        <textarea v-model="draft" maxlength="1600" rows="3" aria-label="Text message" :disabled="busy || stopped" placeholder="Write a text message…" />
        <div class="delivery-capsules"><span>General message</span><span>Publish to: Text</span></div>
        <small>{{ draft.length }}/1600</small>
        <button :disabled="busy || stopped || uncertain || !draft.trim()">Send Text</button>
      </form>
      <p v-if="uncertain">Text acceptance is uncertain. Check Twilio before sending again.</p>
    </template>
  </section>
</template>
<script>
import MessageToolsDrawer from "./MessageToolsDrawer.vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { deleteMaixTextConversation, fetchMaixTextMessages, markMaixTextThreadRead, setMaixTextConversationArchived, sendMaixTextMessage } from "@/firebase/firestore";
export default {
  components: { MessageToolsDrawer },
  props: { threads: { type: Array, default: () => [] }, unassigned: Boolean },
  emits: ["changed"],
  setup(props, { emit, expose }) {
    const busy = ref(false), error = ref(""), deleteId = ref(""), confirmation = ref(""), messages = ref([]), draft = ref(""), uncertain = ref(false);
    let disposed = false;
    const unread = computed(() => props.threads.reduce((total, thread) => total + (thread.unreadCount || 0), 0));
    const stopped = computed(() => props.threads[0]?.optOutStatus === "stopped" || props.threads[0]?.conversationStatus === "archived");
    const load = async () => {
      if (!props.unassigned || !props.threads[0] || disposed) return;
      try { const result = await fetchMaixTextMessages(props.threads[0].id); if (!disposed) messages.value = result; }
      catch (failure) { if (!disposed) error.value = failure.message; }
    };
    const action = async callback => {
      busy.value = true; error.value = "";
      try { await callback(); if (!disposed) { emit("changed"); await load(); } }
      catch (failure) { if (!disposed) error.value = failure.message || "Unable to update texts."; }
      finally { busy.value = false; }
    };
    const markRead = id => action(() => markMaixTextThreadRead(id));
    const archive = thread => action(() => setMaixTextConversationArchived({ threadId: thread.id, archived: thread.conversationStatus !== "archived" }));
    const remove = () => {
      if (confirmation.value !== "CONFIRM") return;
      return action(async () => { await deleteMaixTextConversation({ threadId: deleteId.value, confirmation: confirmation.value }); deleteId.value = ""; });
    };
    const send = () => {
      if (busy.value || stopped.value || uncertain.value || !draft.value.trim()) return;
      return action(async () => {
        try { await sendMaixTextMessage({ threadId: props.threads[0].id, text: draft.value.trim(), contextType: "general" }); draft.value = ""; }
        catch (failure) { uncertain.value = true; throw failure; }
      });
    };
    expose({ refresh: load });
    onMounted(load);
    onBeforeUnmount(() => { disposed = true; });
    return { busy, error, deleteId, confirmation, messages, draft, uncertain, unread, stopped, markRead, archive, remove, send };
  }
};
</script>
<style scoped>
.text-tools { position: relative; flex: 0 0 auto; max-height: none; overflow-y: auto; margin-bottom: 0; padding: 14px; border: 1px solid #dce6e0; border-radius: 10px; }
summary { cursor: pointer; } .thread-tools { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 20px; }
button { padding: 8px 12px; margin: 4px; border: 0; border-radius: 7px; background: #e7f4ed; color: #24553c; cursor: pointer; }
button:disabled { opacity: .5; cursor: default; } input, textarea { box-sizing: border-box; width: 100%; padding: 10px; }
.sms-feed { max-height: 520px; overflow: auto; } article { padding: 12px; margin: 10px 0; background: #f0f3f1; border-radius: 12px; max-width: 85%; }
article.outbound { margin-left: auto; background: #e7f4ed; } article p { white-space: pre-wrap; } small { color: #687b70; }
.unassigned-console { flex: 1; max-height: none; min-height: 0; display: flex; flex-direction: column; }
.unassigned-console .sms-feed { flex: 1 1 0; min-height: 80px; max-height: none; }
.unassigned-console form { flex: 0 0 auto; padding-top: 12px; }
.delivery-capsules { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0; }
.delivery-capsules span { padding: 4px 10px; border-radius: 20px; background: #edf4ef; color: #315d45; font-size: .75rem; }
</style>
